import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * UNE VRAIE FENÊTRE, déplaçable sur un second écran (lot 5.5 bis).
 * ────────────────────────────────────────────────────────────────────────────
 * La maquette (`detachWin` / `renderDetached` / `syncDetached`) fabrique le
 * contenu de la fenêtre en RECOPIANT du HTML à chaque changement, et rappelle
 * les fonctions de la page mère par `opener.`. C'est ce qu'un fichier HTML
 * unique peut faire de mieux.
 *
 * Ici, le contenu est rendu par React dans le document de la fenêtre, à travers
 * un PORTAIL. Trois conséquences, toutes voulues :
 *   1. la synchronisation est acquise — la fenêtre affiche le même état React
 *      que la page, il n'y a pas de recopie à déclencher ni à oublier ;
 *   2. ce qui est détaché reste VIVANT : on filtre et on peint le détail par
 *      mois depuis la fenêtre, et le calendrier de la page mère suit ;
 *   3. un seul rendu du bloc existe à la fois — le contenu n'est pas dupliqué.
 *
 * Le prix à payer est de trois ordres, traité ci-dessous : les feuilles de
 * style ne suivent pas le portail, les événements de souris relâchés dans la
 * fenêtre n'atteignent pas la page mère, et une fenêtre qui survit à sa page
 * mère devient une coquille morte.
 */

/* Le document d'une fenêtre neuve n'a aucun style propre : ces trois règles
   remettent le corps à plat. Tout le reste vient des feuilles de l'application,
   clonées à l'ouverture — le module garde donc exactement son apparence. */
const CSS_CORPS = 'html,body{margin:0;padding:0;background:#f1f5f9;}';

/** Marque posée dans le document de la fenêtre : c'est à elle qu'on la reconnaît. */
const MARQUE = 'pl-detach-racine';

/**
 * Retrouve une fenêtre détachée OUVERTE, après un rechargement de la page mère.
 *
 * `window.open` avec un nom, sans dimensions, ne crée pas de fenêtre surgissante
 * quand une fenêtre porte déjà ce nom : il la retourne. C'est ce qui permet de
 * la reprendre alors qu'aucun clic n'est en cours — un `window.open` classique
 * serait refusé ici. Si le navigateur en ouvre une neuve malgré tout (la
 * précédente ayant été fermée), elle n'a pas notre marque : on la referme et on
 * renvoie `null`, l'appelant en conclut que le bloc n'est plus détaché.
 */
export function reprendreFenetre(nom) {
    let fenetre = null;
    try {
        fenetre = window.open('', 'pl_' + nom);
    } catch {
        return null;
    }
    if (!fenetre) return null;
    try {
        if (fenetre.document?.getElementById(MARQUE)) return fenetre;
        fenetre.close();
    } catch {
        // Fenêtre inaccessible (rare) : on la laisse et on repart de zéro.
    }
    return null;
}

/**
 * ⚠️ LA FENÊTRE EST OUVERTE PAR L'APPELANT, PAS ICI — et c'est la seule façon
 * dont cela puisse marcher. Un navigateur n'autorise `window.open` que dans la
 * pile d'appel du clic qui le demande (« activation par l'utilisateur »).
 * Appelé depuis un `useEffect`, c'est-à-dire APRÈS le rendu, l'appel n'a plus
 * de geste utilisateur derrière lui : Chrome le bloque comme une fenêtre
 * surgissante non sollicitée. C'est le défaut trouvé par Bertrand à la recette
 * du lot 5.5 bis — « le navigateur bloque » alors que la maquette, elle,
 * fonctionnait : `detachWin` y est appelé directement par le `onclick`.
 * Ce composant reçoit donc une fenêtre DÉJÀ OUVERTE et se charge du reste.
 */
function FenetreDetachee({ fenetre, titre, sousTitre, icone, actions, onFermer, children }) {
    const [conteneur, setConteneur] = useState(null);
    // `onFermer` change à chaque rendu du parent (fonction fléchée). L'effet ne
    // doit PAS se rejouer pour autant : réinitialiser le document à chaque frappe
    // au clavier ferait clignoter la fenêtre. On lit la version courante par réf.
    const fermerRef = useRef(onFermer);
    fermerRef.current = onFermer;

    useEffect(() => {
        if (!fenetre || fenetre.closed) return undefined;

        const doc = fenetre.document;
        doc.title = titre + ' — Planning annuel';

        // Une fenêtre rouverte sous le MÊME nom (`pl_dashboard`) n'est pas neuve :
        // le navigateur réutilise celle qui existe, avec son ancien contenu. On
        // repart donc systématiquement d'un document vide.
        doc.head.innerHTML = '';
        doc.body.innerHTML = '';

        // Les styles ne traversent pas un portail : le portail déplace des NŒUDS,
        // pas les feuilles qui les habillent. On clone donc les `<link>` (dont
        // FontAwesome, servi par un CDN) et les `<style>` injectés par le
        // constructeur. Clonés à l'ouverture : une feuille rechargée à chaud
        // pendant le développement ne se propage pas — refermer la fenêtre et la
        // rouvrir suffit à la remettre à jour.
        document.querySelectorAll('link[rel="stylesheet"], style').forEach(noeud => {
            doc.head.appendChild(noeud.cloneNode(true));
        });
        const feuilleCorps = doc.createElement('style');
        feuilleCorps.textContent = CSS_CORPS;
        doc.head.appendChild(feuilleCorps);

        // Le document ne reçoit qu'un point d'ancrage vide : barre de titre
        // comprise, TOUT le contenu est du JSX rendu par le portail. La première
        // version fabriquait cette barre en HTML, avec son échappement à la main
        // et des boutons dont le gestionnaire vieillissait au premier rendu.
        //
        // L'identifiant sert de MARQUE DE FABRIQUE : après un rechargement de la
        // page mère, c'est à lui qu'on reconnaît une fenêtre qui est déjà la
        // nôtre, et qu'on peut donc reprendre au lieu d'en ouvrir une autre.
        const racine = doc.createElement('div');
        racine.id = MARQUE;
        doc.body.appendChild(racine);
        setConteneur(racine);

        // Le glisser-peindre du « Détail par mois » s'arrête sur un `mouseup`
        // écouté par la page mère. Relâchée DANS la fenêtre détachée, la souris
        // ne lui envoie rien : le glissement resterait armé indéfiniment. La
        // maquette a le même relais (`w.addEventListener('mouseup', moisDragEnd)`).
        const relaisMouseUp = () => window.dispatchEvent(new Event('mouseup'));
        fenetre.addEventListener('mouseup', relaisMouseUp);

        // ⚠️ LA PAGE MÈRE SE RECHARGE PLUS SOUVENT QU'ON NE CROIT — et la fenêtre
        // ne doit PAS mourir avec elle. `api.js` appelle `window.location.reload()`
        // dès qu'une requête répond 401, et le serveur de développement recharge
        // à chaque modification de fichier. La première version fermait les
        // fenêtres sur `beforeunload` : une fenêtre détachée disparaissait donc
        // sans raison visible, et il devenait impossible d'en garder deux ouvertes
        // — le défaut relevé par Bertrand.
        //
        // Elles restent donc ouvertes, avec un message qui dit leur état, et la
        // page les REPREND à son prochain montage (voir `reprendreFenetres`).
        const enAttente = () => {
            const zone = fenetre.document?.getElementById(MARQUE);
            if (zone) zone.innerHTML =
                '<div style="padding:28px;font:14px \'Segoe UI\',sans-serif;color:#475569;">'
                + 'La page principale se recharge… cette fenêtre va se rattacher toute seule.</div>';
        };
        window.addEventListener('beforeunload', enAttente);

        // Fermeture par la croix du système : aucun événement n'arrive de façon
        // fiable dans la page mère (`beforeunload` sur la fenêtre fille ne se
        // déclenche pas partout). La surveillance de `closed` est le seul moyen
        // sûr — c'est un test de booléen deux fois par seconde.
        const veille = setInterval(() => {
            if (fenetre.closed && fermerRef.current) fermerRef.current();
        }, 500);

        // ⚠️ CE NETTOYAGE NE FERME PAS LA FENÊTRE — et c'est délibéré.
        // En développement, `React.StrictMode` monte, démonte puis remonte chaque
        // composant pour débusquer les effets mal nettoyés. Un `close()` ici
        // fermait donc la fenêtre une fraction de seconde après son ouverture :
        // « la fenêtre s'affiche et se ferme » (relevé par Bertrand, 2026-08-09).
        // La fenêtre appartient à qui l'a ouverte : c'est `PlanningApp` qui la
        // ferme, sur « Ramener » et en quittant l'écran.
        return () => {
            clearInterval(veille);
            fenetre.removeEventListener('mouseup', relaisMouseUp);
            window.removeEventListener('beforeunload', enAttente);
            setConteneur(null);
        };
        // Volontairement joué une seule fois par fenêtre : `titre` et `icone` ne
        // changent pas pendant la vie d'une fenêtre détachée.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fenetre]);

    if (!conteneur) return null;
    return createPortal(
        <div className="pl-detach">
            <div className="pl-detach-h">
                <b><i className={'fa-solid ' + (icone || 'fa-window-restore')} /> {titre}</b>
                {/* Le contexte suit le bloc : sur un second écran, plus rien ne dit
                    de quel département ni de quelle année on lit les chiffres. */}
                {sousTitre && <span className="pl-detach-sub">{sousTitre}</span>}
                <span className="pl-grow" />
                {actions}
                {/* Doublon volontaire de la croix du système : une fenêtre poussée
                    sur un second écran a parfois sa barre de titre hors de vue. */}
                <button className="pl-btn pl-btn-sm" onClick={onFermer} title="Fermer et ramener le bloc dans sa carte">
                    <i className="fa-solid fa-xmark" /> Fermer
                </button>
            </div>
            <div className="pl-detach-b">{children}</div>
        </div>,
        conteneur
    );
}

export default FenetreDetachee;
